import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Space, Divider, Tag, Checkbox, Modal } from 'antd';
import { MailOutlined, LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MolStarViewer from '../../components/results/protein/structure/MolStarViewer';

const { Title, Text } = Typography;

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [termsVisible, setTermsVisible] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agree = Form.useWatch('agree', form);
  const [pdbId, setPdbId] = useState<string | null>(null);
  const [pdbData, setPdbData] = useState<string | null>(null);
  const LOCAL_BASE = '/structures';
  
  // 本地结构文件列表
  const localStructures = [
    '8tlm_ag.pdb',
    'antibody_heavy_chain.pdb',
    'equidock_final_result.pdb',
    'gapdh_ab0669.pdb',
    'sample_l_b_EQUIDOCK.pdb'
  ];

  // 如果已经登录，重定向到目标页面或首页
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = searchParams.get('returnUrl') || '/';
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  // 随机选择本地结构文件
  const getRandomLocalStructure = (): string => {
    return localStructures[Math.floor(Math.random() * localStructures.length)];
  };

  // 加载随机本地结构文件
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // 随机选择一个本地文件
        const fileName = getRandomLocalStructure();
        const fileUrl = `${LOCAL_BASE}/${fileName}`;
        
        if (!mounted) return;
        setPdbId(fileName.replace(/\.(pdb|cif)$/i, ''));

        // 加载文件内容
        try {
          const response = await fetch(fileUrl, { cache: 'no-store' });
          if (response.ok) {
            const text = await response.text();
            if (!mounted) return;
            setPdbData(text);
          }
        } catch (error) {
          console.error('加载结构文件失败:', error);
        }
      } catch (e) {
        console.error('初始化结构失败:', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onFinish = async (values: { email: string }) => {
    const agreed = form.getFieldValue('agree');
    if (!agreed) {
      message.warning('请先阅读并勾选同意《用户服务协议与使用须知》');
      return;
    }
    setLoading(true);
    try {
      const result = await login(values.email);
      if (result.success) {
        message.success(result.message);
        form.resetFields();
        const returnUrl = searchParams.get('returnUrl') || '/';
        navigate(returnUrl, { replace: true });
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '1320px',
          height: '85vh',
          minHeight: '700px',
          borderRadius: '16px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06))',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 16px 60px rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255,255,255,0.15)',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: 0, height: '100%' }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2.6fr 1fr',
          gap: 0,
          height: '100%'
        }}>
          {/* 左侧 生命科学叙事 */}
          <div style={{
            padding: '40px',
            background: 'rgba(255,255,255,0.95)',
            borderRight: '1px solid rgba(200,200,200,0.3)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ marginBottom: 16 }}>
              <Tag color="#2db7f5" style={{ borderRadius: 8, fontWeight: 700, padding: '4px 10px' }}>LIFE SCIENCE • AI</Tag>
            </div>
            <Title level={2} style={{ color: '#1a1a1a', margin: 0, marginBottom: 8, fontSize: 36, lineHeight: 1.15 }}>
              CUSABIO生命科学计算平台
            </Title>
            <Text style={{ color: '#666', fontSize: 15, display: 'block', marginBottom: 20 }}>
              序列分析、蛋白结构预测、大分子设计、相互作用分析等。
            </Text>

            <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
              {pdbData ? (
                <MolStarViewer 
                  pdbData={pdbData} 
                  height="100%"
                  initialRepresentation="surface"
                  autoRotate={true}
                  minimal={true}
                  showHoverInfo={false}
                  disableAllMouseEvents={true}
                  randomInitialRepresentation={true}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>加载结构中…</div>
              )}
            </div>
          </div>

          {/* 右侧 登录表单 */}
          <div style={{ 
            padding: '40px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <Title level={3} style={{ marginBottom: 6, color: '#fff' }}>
                登录生物计算平台
              </Title>
              <Text type="secondary" style={{ color: 'rgba(255,255,255,0.75)' }}>
                <div style={{ textAlign: 'center' }}>
                  <Space direction="vertical" size="small">
                    <Text type="secondary" style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
                      填写合法邮箱即可登录平台
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
                      无需发送邮件或记忆密码
                    </Text>
                  </Space>
                </div>
              </Text>
            </div>

            <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ agree: false }}>
              <Form.Item
                name="email"
                label={<span style={{ color: '#fff' }}>邮箱地址</span>}
                rules={[
                  { required: true, message: '请输入邮箱地址' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="输入您的邮箱地址"
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>

              <Form.Item
                name="agree"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('请先勾选同意服务条款')),
                  },
                ]}
              >
                <Checkbox style={{ color: '#fff', fontWeight: 500, alignItems: 'flex-start' }}>
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: '22px' }}>
                    <span>我已阅读并同意</span>
                    <Button 
                      type="link" 
                      style={{ 
                        padding: 0,
                        height: 'auto',
                        lineHeight: '22px',
                        color: '#fff',
                        fontWeight: 600,
                        textDecoration: 'none',
                        textAlign: 'left'
                      }} 
                      onClick={() => setTermsVisible(true)}
                    >
                      《用户服务协议与使用须知》
                    </Button>
                  </span>
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                  icon={<LoginOutlined />}
                  style={{
                    borderRadius: 10,
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600,
                    background: '#fff',
                    color: '#1e40af',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                  disabled={!agree || loading}
                >
                  {loading ? '登录中...' : '直接登录'}
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <img
                  src="/生命科学计算平台答疑群.png"
                  alt="官方交流群"
                  style={{
                    width: 160,
                    height: 160,
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
                <div style={{
                  marginTop: 8,
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 13,
                  fontWeight: 500
                }}>
                  扫码加入官方交流反馈群
                </div>
              </div>
            </Form>
          </div>
        </div>
      </Card>

      <Modal
        title={<Title level={4}>用户服务协议与使用须知</Title>}
        open={termsVisible}
        onCancel={() => setTermsVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setTermsVisible(false)}>取消</Button>,
          <Button
            key="agree"
            type="primary"
            onClick={() => {
              form.setFieldsValue({ agree: true });
              setTermsVisible(false);
            }}
          >
            我已阅读并同意
          </Button>,
        ]}
        width={800}
      >
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>欢迎您使用本平台！</Text>
            <Text>
              在您登录并使用本服务前，请您务必仔细阅读并充分理解本《用户服务协议与使用须知》（以下简称“本协议”）的各条款内容，
              <strong>特别是那些以加粗形式展示的，与您的权益有重大关系的条款，包括但不限于免除或限制我们责任的条款。</strong>
            </Text>
            <Text>
              当您点击“同意”按钮、勾选同意选项或实际登录、使用本服务时，即表示您已完整阅读、充分理解并同意接受本协议的全部内容。
              如果您不同意本协议的任何内容，请立即停止使用本服务。
            </Text>

            <Title level={5}>一、 服务说明</Title>
            <Text>
              本协议是您与“本平台”之间关于使用本平台各项服务的法律协议。本平台致力于为用户提供核酸、蛋白、抗体设计工具与服务。
            </Text>

            <Title level={5}>二、 核心条款（请重点阅读）</Title>
            <Text>您理解并同意，为了保证平台的稳定运行和持续优化，我们将遵循以下原则：</Text>
            <Text strong>1. 数据清理政策</Text>
            <Text>
              为保证服务器的稳定运行和资源优化，本平台将对用户数据（包括但不限于您上传的文件、生成的报告、历史记录、操作日志等）进行 不定期清理。我们没有义务为您永久保存任何数据。
            </Text>
            <Text strong>2. 用户数据备份责任</Text>
            <Text>
              您对您在本服务中创建、上传、存储或生成的任何数据负有 唯一的、完全的责任。我们强烈建议并要求您对您认为重要的任何数据，进行 及时的下载、导出和本地备份。因本平台执行数据清理、服务器故障、系统更新或任何其他不可抗力因素导致的数据丢失、损坏或无法访问，本平台不承担任何数据恢复或赔偿责任。
            </Text>
            <Text strong>3. 免责声明</Text>
            <Text>
              本服务提供的数据、信息或分析结果 仅供您参考使用。本平台不对其准确性、完整性、及时性或特定用途的适用性做出任何明示或暗示的保证。
            </Text>
            <Text>
              您基于本服务提供的数据或结果所做出的任何决策、行为（例如商业投资、学术研究、个人判断等）及其产生的 任何直接或间接的后果（包括但不限于利润损失、数据丢失、业务中断等），均由您自行承担。本平台对此不承担任何形式的法律责任。
              本服务是“按现状”和“按可用”的基础提供的。我们不保证服务不会中断或没有错误。
            </Text>

            <Title level={5}>三、 用户行为规范</Title>
            <Text>
              您承诺不利用本服务进行任何违法或不正当的活动，包括但不限于：上传、传播含有违反国家法律法规内容的信息；侵犯他人知识产权、商业秘密等合法权益；从事任何可能危及本平台系统安全的行为。
              如您违反上述规定，本平台有权独立判断并随时暂停或终止向您提供服务，且无需事先通知。
            </Text>

            <Title level={5}>四、 协议的变更与终止</Title>
            <Text>
              我们有权根据业务需要不时修订本协议，并将通过网站公告、系统消息或您登录时弹窗提示等方式通知您。修订后的协议一经公布，即刻生效。若您在协议修订后继续使用本服务，即表示您已接受修订后的协议。
            </Text>

            <Title level={5}>五、 其他</Title>
            <Text>本协议的最终解释权归 cusabio所有。</Text>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;